import React, { useState, useEffect } from 'react';
import { ClipboardList, ArrowRight, X, Calendar } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';

// Solo las que todavía se pueden responder. El backend marca 'pendiente' cuando la
// persona está invitada, no respondió y la encuesta sigue disponible (no venció);
// sin ese dato (respuesta vieja en caché) se revisa la fecha límite aquí.
const estaPendiente = (e) => (e.estadoUsuario
    ? e.estadoUsuario === 'pendiente'
    : !e.yaRespondio && e.activa && e.estaInvitado && (!e.fechaLimite || new Date(e.fechaLimite) > new Date()));

const EncuestasPendientesWidget = () => {
    const [encuestas, setEncuestas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const key = 'widget_dismiss_encuestas';
        const val = localStorage.getItem(key);
        if (val === new Date().toDateString()) setDismissed(true);
    }, []);

    useEffect(() => {
        axiosInstance.get('/api/encuestas-producto')
            .then(res => setEncuestas(res.data.filter(estaPendiente)))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleDismiss = () => {
        localStorage.setItem('widget_dismiss_encuestas', new Date().toDateString());
        setDismissed(true);
    };

    if (loading || encuestas.length === 0 || dismissed) return null;

    const urgente = encuestas.sort((a, b) => {
        if (a.fechaLimite && b.fechaLimite) return new Date(a.fechaLimite) - new Date(b.fechaLimite);
        if (a.fechaLimite) return -1;
        if (b.fechaLimite) return 1;
        return 0;
    })[0];

    return (
        <section className="relative rounded-2xl overflow-hidden border border-teal-200 shadow-sm group">
            <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 z-20 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm text-gray-400 hover:text-gray-600 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                title="Ocultar por hoy"
            >
                <X size={14} />
            </button>

            <div className="bg-teal-600 px-5 py-4 text-white">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                        <ClipboardList size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-lg">Encuesta pendiente</h3>
                        <p className="text-white/80 text-sm">
                            {encuestas.length === 1 ? '1 encuesta por responder' : `${encuestas.length} encuestas por responder`}
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-teal-50 p-5">
                <div
                    className="bg-white rounded-xl p-4 border border-teal-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow mb-4"
                    onClick={() => navigate('/encuestas-producto')}
                >
                    <p className="text-sm font-medium text-gray-900 leading-snug">{urgente.titulo}</p>
                    {urgente.descripcion && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{urgente.descripcion}</p>
                    )}
                    {urgente.fechaLimite && (
                        <p className="flex items-center gap-1.5 text-xs text-amber-600 mt-2 font-medium">
                            <Calendar size={12} />
                            Hasta {new Date(urgente.fechaLimite).toLocaleDateString('es-VE')}
                        </p>
                    )}
                </div>

                {encuestas.length > 1 && (
                    <p className="text-xs text-teal-700 text-center mb-3">
                        y {encuestas.length - 1} más...
                    </p>
                )}

                <button
                    onClick={() => navigate('/encuestas-producto')}
                    className="w-full py-3 rounded-xl bg-gray-900 text-white font-medium flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors text-sm"
                >
                    Ver encuestas
                    <ArrowRight size={16} />
                </button>
            </div>
        </section>
    );
};

export default EncuestasPendientesWidget;
